import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup} from '@angular/forms';
import { AuthService } from '@core/auth/auth.service';
import { CourseTaskQuestion } from '@core/models/course/course-task-question.model';
import { CourseTask } from '@core/models/course/course-task.model';
import { FormQuestion } from '@core/models/form-question';
import { CourseService } from '@core/services/course.service';
import { LoadingService } from '@core/services/loading.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { Message } from '@modules/communication/models/message';
import { CommunicationService } from '@modules/communication/services/communication.service';
import { ArticleReadTimer } from '@modules/knowledge/services/article-read-timer';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { filter, skip } from 'rxjs';

@Component({
    selector: 'app-message-viewer',
    templateUrl: './message-viewer.component.html',
    styleUrls: ['./message-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MessageViewerComponent implements OnInit{
  @ViewChild('dialog') dialog!: DialogComponent;
  @ViewChild('taskDialog') taskDialog!: DialogComponent;
  @ViewChild('imageDialog') imageDialog!: DialogComponent;
  message!: Message;
  failedTesting = false;
  startTime: number|null = null;
  secondsRead: number = 0;
  loading:boolean = true;
  loadingQuestion:boolean = true;
  showSummary:boolean = false;
  underStood:boolean = false;
  imageZoom:boolean = false;
  questions:FormQuestion[] = [];
  currentQuestion:FormQuestion = {} as FormQuestion;
  task!:CourseTask;

  currentMessageIndex = 0;
  hideMessageContent:boolean = false;
  messages:Message[] = [];


  form:FormGroup = new FormGroup({
    questions:new FormArray([])
  });

  constructor(
    private communicationService: CommunicationService,
    private loadingService: LoadingService,
    private snackBarService: SnackBarService,
    private courseService: CourseService,
    private fb: FormBuilder,
    private authService: AuthService,
    private articleReadTimer:ArticleReadTimer
  ) {
  }

  ngOnInit(): void {

    // Always listen: this component is created once, also on /login, and must still show messages after an in-app login.
    this.communicationService.messagesToShow$.subscribe(messages => {
      this.messages = messages;
      // A fetch still in flight at logout must not open on the login page.
      if (messages.length > 0 && this.authService.IsLoggedIn) {
        this.show(0);
      }
    });

    // skip(1): the initial value arrives before the dialogs exist; only react to an actual logout.
    this.authService.isAuthenticated().pipe(skip(1), filter(loggedIn => !loggedIn)).subscribe(() => this.close());

    if (this.authService.IsLoggedIn) {
      this.communicationService.getUnreadMessages();
    }
  }

  show(index:number) {
    this.message = this.messages[index];
    this.message.article = (this.message.article??'').split('/').pop();
    this.underStood = this.message.read;
    this.hideMessageContent = false;
    this.failedTesting = false;
    this.currentMessageIndex = index;
    this.dialog.show();
    this.startTime = new Date().getTime();
    this.secondsRead = 0;
  }

  close(): void {
    // The dialog keeps the article component alive when hidden, so end its read time here.
    this.articleReadTimer.stop();
    this.taskDialog.hide();
    this.dialog.hide();
  }

  markAsRead(): void {
    
    if(this.message.article){
      this.articleReadTimer.stop();
    }
    
    if(this.message.read){
      this.loadingService.stopLoading();
      this.close();
    }else{
      
      this.loadingService.startLoading();
      const secondsRead = this.calculateSecondsRead();
      let answers: Record<string, string[]>|null = null;
      if (this.message.task && this.questions) {
        if (!this.canTurnIn) {
          this.snackBarService.error();
          return;
        }
  
        answers = {};
        for (const question of this.questions) {
          answers[question.id] = question.answer;
        }
      }
      
      this.communicationService.markAsRead(this.message, secondsRead, answers).subscribe(success => {
        this.loadingService.stopLoading();
        if (success) {
          if(this.messages.length > (this.currentMessageIndex + 1)){
            this.message.read = true;
            this.show(this.currentMessageIndex + 1);
          }else{
            this.close();
          }
        } else {
          this.taskDialog.hide();
          this.failedTesting = true;
        }
      }, error =>{
        this.loadingService.stopLoading();
        this.snackBarService.error();
      });
    }
  }

  private calculateSecondsRead(): number {
    const stopTime = new Date().getTime();
    return Math.round((stopTime - (this.startTime ?? 0)) / 1000);
  }

  retryTask(): void {
    this.gotoTask();
  }

  skipMessage(){
    if(this.messages.length > (this.currentMessageIndex + 1)){

      this.show(this.currentMessageIndex + 1);
    }else{
      this.close();
    }
  }

  gotoTask(): void {
    if (this.message.task) {
      this.secondsRead += this.calculateSecondsRead()
      this.loadingService.startLoading();
      this.loading = true;
      const taskId = this.message.task.split('/').pop() as string;
      this.courseService.getGlobalTask(taskId).subscribe(task => {
        this.task = task;
        this.showSummary = false;
        if(task.questions.length > 0){
          this.questions = task.questions.map(this.formatFormQuestion);
          this.questions.map(question => this.addQuestionControl(question));
          this.setAnswerForm(this.questions[0]);
        }
        this.loading = false;
        this.loadingService.stopLoading();
        this.failedTesting = false;
        this.taskDialog.show();
      });
    }
  }

  addQuestionControl(q:FormQuestion){
    const questionControl = this.fb.group({
      id: [q.id],
      name: [q.name],
      answer: ['']
    });
    (this.form.get('questions') as FormArray).push(questionControl);
  }

  setAnswer(){
    if(this.currentQuestion.type == 'multiple-answer'){
      this.currentQuestion.answer = this.currentQuestion.options.filter(x => x.selected).map( x => x.id);
    }

    if(this.currentQuestion.type == 'open' && this.currentQuestion.answer[0] == ''){
      this.currentQuestion.answer = [];
    }
  }

  get canGoNext():boolean{
    return (!this.currentQuestion.required || (this.currentQuestion.required && this.currentQuestion.answer.length > 0)) && !this.loading;
  }

  get canGoBack():boolean{
    let currentIndex = this.questions.findIndex(x => x.id == this.currentQuestion.id);
    return currentIndex > 0 || this.currentQuestion.feedback != null;
  }

  get canTurnIn():boolean{
    return this.showSummary;
  }

  goNext(){
    this.loadingQuestion = true;
    if(this.currentQuestion.type != 'open' && this.currentQuestion.required){
      if(this.task.feedbackPerQuestion && this.currentQuestion.feedback == null){
        this.courseService.getQuestionFeedback(this.currentQuestion).subscribe((x:any) =>{
          this.currentQuestion.feedback = x.feedback;
          this.currentQuestion.feedbackMode = true;
          this.currentQuestion.options.forEach(option => {
            option.correct =  x.correctAnswers.find((id:string) => id == option.id);
          });
          this.loadingQuestion = false;
        });
      }else{
        this.moveOn();
      }
    }else{
      this.moveOn();
    }
  }

  moveOn(){
    let currentIndex = this.questions.findIndex(x => x.id == this.currentQuestion.id);
    if(currentIndex + 1 < this.questions.length){
      this.setAnswerForm(this.questions[currentIndex+1]);
    }else{
      //show summary
      this.showSummary = true;
    }
  }

  goBackToMessage(): void {
    this.startTime = new Date().getTime();
    this.taskDialog.hide();
  }

  goBack(){
    if(this.showSummary){
      this.showSummary = false;
    } else{
      if(this.currentQuestion.feedbackMode){
        this.setAnswerForm(this.currentQuestion);
      }else{
        let currentIndex = this.questions.findIndex(x => x.id == this.currentQuestion.id);
        this.setAnswerForm(this.questions[currentIndex-1]);
      }
    }
  }

  goToQuestion(i:number){
    this.setAnswerForm(this.questions[i]);
    this.showSummary = false;

  }

  setAnswerForm(q:FormQuestion){
    this.currentQuestion = q;
    this.currentQuestion.feedback = null;
    this.currentQuestion.feedbackMode = false;
    this.loadingQuestion = false;
    let tabmenu = document.getElementById("tab_"+q.id);
    if(tabmenu){
      tabmenu.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'});
    }
  }

  formatFormQuestion(question: CourseTaskQuestion, position:number): FormQuestion
  {
    return {
      id: question.id,
      name: question.name,
      description: question.description,
      type: question.type,
      required: question.required,
      feedbackMode: false,
      feedback: null,
      answer: [],
      image:question.image,
      position:position,
      article:question.article,
      options: question.options.map(opt => {
        return {...opt, selected: false};
      }),
    }
  }
}
